
/**
 * Redirect configuration for Grandpal website
 * Maps old URL patterns to new URL patterns
 */

import { NursingHome } from '@/hooks/use-nursing-home';

// Type definitions
export interface RedirectRule {
  pattern: RegExp;
  toPath: string | ((matches: RegExpMatchArray) => string);
  type: 'static' | 'dynamic';
}

// Redirect rules
export const redirectRules: RedirectRule[] = [
  // County specific nursing home pages
  {
    pattern: /^\/([a-z-]+)-nursing-homes\/([a-z-]+)$/,
    toPath: (matches: RegExpMatchArray) => `/nursing-homes/${matches[1]}/${matches[2]}`,
    type: 'dynamic',
  },
  
  // Legacy ireland path for nursing homes
  {
    pattern: /^\/ireland\/([a-z0-9-]+)$/,
    toPath: (matches: RegExpMatchArray) => `/nursing-homes/lookup/${matches[1]}`,
    type: 'dynamic',
  },

  // Blog posts
  {
    pattern: /^\/post\/([a-z0-9-]+)$/,
    toPath: (matches: RegExpMatchArray) => `/blog/${matches[1]}`,
    type: 'dynamic',
  },
  
  // Calculator redirect
  {
    pattern: /^\/calculator$/,
    toPath: `/fair-deal-calculator`,
    type: 'static',
  },

  // Guide redirects
  {
    pattern: /^\/guide$/,
    toPath: `/guides`,
    type: 'static',
  },
  
  {
    pattern: /^\/guide\/([a-z0-9-]+)$/,
    toPath: (matches: RegExpMatchArray) => `/guides/${matches[1]}`,
    type: 'dynamic',
  },

  // Legal redirects
  {
    pattern: /^\/legal\/terms-of-use$/,
    toPath: `/terms-of-use`,
    type: 'static',
  },
  
  // New redirect rule for nursing-homes-[county]
  {
    pattern: /^\/nursing-homes-([a-z-]+)$/,
    toPath: (matches: RegExpMatchArray) => `/nursing-homes/${matches[1]}`,
    type: 'dynamic',
  },
  
  // New redirect rule for nursing-homes-in-[county]
  {
    pattern: /^\/nursing-homes-in-([a-z-]+)$/,
    toPath: (matches: RegExpMatchArray) => `/nursing-homes/${matches[1]}`,
    type: 'dynamic',
  },
  
  // Forms redirects including fair-deal calculator form
  {
    pattern: /^\/forms\/fair-deal-and-nursing-home-cost-calculator$/,
    toPath: `/fair-deal-calculator`,
    type: 'static',
  },
  
  {
    pattern: /^\/forms\/fair-deal-and-nursing-home-cost-calculator-results$/,
    toPath: `/fair-deal-calculator`,
    type: 'static',
  },
  
  {
    pattern: /^\/forms\/nursing-home-waitlist$/,
    toPath: `/waitlist`,
    type: 'static',
  },
];

/**
 * Find a matching redirect rule for the given pathname
 * @param pathname Current URL path
 */
export function findRedirect(pathname: string): { 
  redirectTo: string | null;
  isDynamic: boolean;
} {
  // Remove trailing slash for consistency
  const normalizedPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  
  for (const rule of redirectRules) {
    const matches = normalizedPath.match(rule.pattern);
    
    if (matches) {
      const redirectTo = typeof rule.toPath === 'function'
        ? rule.toPath(matches)
        : rule.toPath;
      
      return { 
        redirectTo,
        isDynamic: rule.type === 'dynamic'
      };
    }
  }
  
  return { redirectTo: null, isDynamic: false };
}

/**
 * Create a redirect URL for a nursing home based on its data
 * @param nhSlug Nursing home slug
 * @param nursingHomeData Nursing home data from API
 */
export function createNursingHomeRedirectUrl(
  nhSlug: string, 
  nursingHomeData?: NursingHome
): string {
  // If we have the nursing home data and its county, we can create the proper URL
  if (nursingHomeData?.county_slug) {
    return `/nursing-homes/${nursingHomeData.county_slug}/${nhSlug}`;
  }
  
  // If we don't have the data, return a fallback URL that will handle the lookup
  return `/nursing-homes/lookup/${nhSlug}`;
}
